import { forwardRef, useState, useEffect, useRef, useCallback } from 'react'
import DatePickerImport from 'react-datepicker'
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker'
// react-datepicker v7 may export the component as .default in some bundler configs
const DatePicker = (DatePickerImport as any).default || DatePickerImport
import 'react-datepicker/dist/react-datepicker.css'
import { Box, Flex, IconButton, Input } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate()
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max)
}

/** Format a Date to DD-MM-YYYY */
function formatDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

/** Parse DD-MM-YYYY to Date or null */
function parseInputDate(text: string): Date | null {
  const m = text.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (!m) return null
  const day = parseInt(m[1], 10)
  const month = parseInt(m[2], 10)
  const year = parseInt(m[3], 10)
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(month, year)) return null
  if (year < 1900 || year > 2100) return null
  return new Date(year, month - 1, day, 12, 0, 0)
}

interface CustomDateInputProps {
  className?: string
  onBlur?: () => void
  onChange?: (value: Date | null) => void
  containerRef?: React.RefObject<HTMLElement>
  defaultValue?: Date | null
  placeholder?: string
  shouldDisableDate?: (date: Date) => boolean
  required?: boolean
}

export const CustomDateInput = forwardRef<HTMLDivElement, CustomDateInputProps>(
  ({ onChange, defaultValue, placeholder, shouldDisableDate, required }, ref) => {
    const [value, setValue] = useState<Date | null>(defaultValue ?? null)
    const [inputText, setInputText] = useState(defaultValue ? formatDate(defaultValue) : '')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue)
        setInputText(formatDate(defaultValue))
      }
    }, [defaultValue])

    // Called when user picks from calendar
    const handleCalendarChange = (date: Date | null) => {
      setValue(date)
      setInputText(date ? formatDate(date) : '')
      onChange?.(date)
    }

    const prevTextRef = useRef(inputText)
    useEffect(() => {
      prevTextRef.current = inputText
    }, [inputText])

    // Mask logic for typed input
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value

      // Allow clearing
      if (raw === '') {
        setInputText('')
        setValue(null)
        onChange?.(null)
        return
      }

      let allDigits = raw.replace(/[^0-9]/g, '')
      const prevDigits = prevTextRef.current.replace(/[^0-9]/g, '')
      const wasComplete = prevDigits.length === 8

      // If the field was complete and user typed more, start fresh with only new digits
      if (wasComplete && allDigits.length > 8) {
        let newDigits = ''
        let pi = 0
        for (let i = 0; i < allDigits.length; i++) {
          if (pi < prevDigits.length && allDigits[i] === prevDigits[pi]) {
            pi++
          } else {
            newDigits += allDigits[i]
          }
        }
        allDigits = newDigits || allDigits.slice(-1)
      }

      let digits = allDigits.slice(0, 8)
      const parts: string[] = [] // [DD, MM, YYYY]

      // ── Day (digits 0-1) ──
      if (digits.length >= 1) {
        const d0 = parseInt(digits[0], 10)
        if (d0 > 3) {
          // Single digit > 3 can't start a valid day → auto-prefix with 0
          digits = '0' + digits
        }
      }
      if (digits.length >= 2) {
        let dd = parseInt(digits.slice(0, 2), 10)
        dd = clamp(dd, 1, 31)
        parts.push(String(dd).padStart(2, '0'))
      } else if (digits.length === 1) {
        parts.push(digits[0])
      }

      // ── Month (digits 2-3) ──
      if (digits.length >= 3) {
        const m0 = parseInt(digits[2], 10)
        if (m0 > 1) {
          // Single digit > 1 can't start a valid month → auto-prefix with 0
          digits = digits.slice(0, 2) + '0' + digits.slice(2)
        }
      }
      if (digits.length >= 4) {
        let mm = parseInt(digits.slice(2, 4), 10)
        mm = clamp(mm, 1, 12)
        parts[1] = String(mm).padStart(2, '0')

        // Re-validate day against this month
        const dd = parseInt(parts[0], 10)
        const yyyy = digits.length >= 8 ? parseInt(digits.slice(4, 8), 10) : new Date().getFullYear()
        const maxDay = daysInMonth(mm, yyyy)
        if (dd > maxDay) {
          parts[0] = String(maxDay).padStart(2, '0')
        }
      } else if (digits.length === 3) {
        parts.push(digits[2])
      }

      // ── Year (digits 4-7) ──
      if (digits.length > 4) {
        parts[2] = digits.slice(4, Math.min(digits.length, 8))
      }

      // Build result with separators
      let result = parts[0] || ''
      if (parts.length > 1) result += '-' + (parts[1] || '')
      if (parts.length > 2) result += '-' + (parts[2] || '')

      // Re-extract final digit count
      const finalDigits = result.replace(/[^0-9]/g, '')

      setInputText(result)

      // If complete (8 digits), parse and sync to calendar
      if (finalDigits.length === 8) {
        const parsed = parseInputDate(result)
        if (parsed) {
          setValue(parsed)
          onChange?.(parsed)
        }
      }
    }, [onChange])

    const today = new Date()

    return (
      <Box ref={ref} w="100%" sx={{
        '.react-datepicker-popper': { zIndex: 9999 },
        '.react-datepicker-wrapper': { width: '100%' },
        '.react-datepicker__input-container': { width: '100%' },
        '.react-datepicker': {
          fontFamily: 'inherit',
          borderColor: '#ECEFF3',
          borderRadius: '12px',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
        },
        '.react-datepicker__header': {
          bg: 'white',
          borderBottom: '1px solid #ECEFF3',
          p: '0',
        },
        '.react-datepicker__header--custom': { p: '0' },
        '.react-datepicker__day-names': { mt: '4px' },
        '.react-datepicker__day-name': {
          fontSize: '11px', fontWeight: '500', color: '#808897',
          width: '32px', lineHeight: '32px',
        },
        '.react-datepicker__month': { m: '4px' },
        '.react-datepicker__day': {
          fontSize: '13px', fontWeight: '500', color: '#272835',
          width: '32px', lineHeight: '32px', borderRadius: '8px',
          margin: '1px', transition: 'all 0.15s ease', cursor: 'pointer',
          '&:hover': { bg: '#f6f8fa', borderRadius: '8px' },
        },
        '.react-datepicker__day--selected': {
          bg: '#5d5ecb !important', color: 'white !important',
          borderRadius: '8px', fontWeight: '600',
          '&:hover': { bg: '#4a4ba2 !important' },
        },
        '.react-datepicker__day--keyboard-selected': {
          bg: 'transparent', color: '#272835',
        },
        '.react-datepicker__day--disabled': {
          color: '#C1C7CF !important', cursor: 'not-allowed',
          '&:hover': { bg: 'transparent' },
        },
        '.react-datepicker__day--outside-month': { color: '#C1C7CF' },
        '.react-datepicker__triangle': { display: 'none' },
        '.react-datepicker__month-container': { float: 'none' },
        '.react-datepicker__day--today': {
          fontWeight: '500', color: '#272835', bg: 'transparent',
        },
      }}>
        <DatePicker
          selected={value}
          openToDate={value || new Date()}
          onChange={handleCalendarChange}
          dateFormat="dd-MM-yyyy"
          filterDate={shouldDisableDate ? (date: Date) => !shouldDisableDate(date) : undefined}
          popperProps={{ strategy: 'fixed' }}
          placeholderText={placeholder || 'DD-MM-YYYY'}
          customInput={
            <Input
              ref={inputRef}
              value={inputText || undefined}
              onChange={handleInputChange}
              placeholder={placeholder || 'DD-MM-YYYY'}
              bg={required ? 'primary.0' : undefined}
              borderColor={required ? 'primary.50' : undefined}
              _focus={required ? { borderColor: 'primary.100', bg: 'white' } : undefined}
            />
          }
          renderDayContents={(day: number, date?: Date) => {
            if (!date) return <Box as="span">{day}</Box>
            const isToday =
              date.getDate() === today.getDate() &&
              date.getMonth() === today.getMonth() &&
              date.getFullYear() === today.getFullYear()
            return (
              <Box as="span" style={isToday ? {
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '28px', height: '28px', borderRadius: '8px',
                backgroundColor: '#efeffa', color: '#5d5ecb', fontWeight: 700,
              } : undefined}>
                {day}
              </Box>
            )
          }}
          renderCustomHeader={({
            date, changeYear, changeMonth,
            decreaseMonth, increaseMonth,
            prevMonthButtonDisabled, nextMonthButtonDisabled,
          }: ReactDatePickerCustomHeaderProps) => (
            <Flex align="center" justify="space-between" px="8px" py="10px">
              <IconButton
                aria-label="Previous month"
                icon={<ChevronLeftIcon boxSize={5} />}
                variant="ghost" size="xs" minW="28px" h="28px"
                color="#666D80" borderRadius="6px"
                _hover={{ bg: '#f6f8fa' }}
                onClick={decreaseMonth}
                isDisabled={prevMonthButtonDisabled}
              />
              <Flex align="center" gap="2px">
                <select
                  value={date.getMonth()}
                  onChange={(e) => changeMonth(Number(e.target.value))}
                  style={{
                    appearance: 'none', WebkitAppearance: 'none',
                    border: 'none', background: 'transparent',
                    fontWeight: 600, fontSize: '14px', color: '#0D0D12',
                    cursor: 'pointer', padding: '2px 4px', borderRadius: '6px',
                    outline: 'none', fontFamily: 'inherit',
                  }}
                >
                  {MONTHS.map((month, i) => (
                    <option key={i} value={i}>{month}</option>
                  ))}
                </select>
                <select
                  value={date.getFullYear()}
                  onChange={(e) => changeYear(Number(e.target.value))}
                  style={{
                    appearance: 'none', WebkitAppearance: 'none',
                    border: 'none', background: 'transparent',
                    fontWeight: 600, fontSize: '14px', color: '#666D80',
                    cursor: 'pointer', padding: '2px 4px', borderRadius: '6px',
                    outline: 'none', fontFamily: 'inherit',
                  }}
                >
                  {Array.from({ length: 121 }, (_, i) => {
                    const y = new Date().getFullYear() - 100 + i
                    return <option key={y} value={y}>{y}</option>
                  })}
                </select>
              </Flex>
              <IconButton
                aria-label="Next month"
                icon={<ChevronRightIcon boxSize={5} />}
                variant="ghost" size="xs" minW="28px" h="28px"
                color="#666D80" borderRadius="6px"
                _hover={{ bg: '#f6f8fa' }}
                onClick={increaseMonth}
                isDisabled={nextMonthButtonDisabled}
              />
            </Flex>
          )}
        />
      </Box>
    )
  }
)
